import CatalogEmptyState from '@assets/icons/catalog-empty-state.svg';
import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import ScanIcon from '@assets/icons/scan.svg';
import SearchIcon from '@assets/icons/search.svg';
import {
  ALL_CATEGORIES_FILTER,
  ALL_CATEGORY_VALUE,
  NULL_CATEGORY_VALUE,
} from '@/utils/category';
import { AuthStackParams, CatalogStackParams } from '@/navigators/types';
import { CATALOG_ROUTES } from '@/navigators/routes';
import { CatalogCarouselsLoading } from '@/components/catalog-search/catalog-carousels-loading';
import { CatalogCategoryList } from '@/components/catalog-search/catalog-category-list';
import { CatalogCategoryType } from '@/contexts/item-context-types';
import { CatalogItemListLoading } from '@/components/catalog-search/catalog-item-list-loading';
import { CatalogSidebarLoading } from '@/components/catalog-search/catalog-sidebar-loading';
import { CategoryCarousels } from '@/components/catalog-search/catalog-carousels';
import { CompositeNavigationProp } from '@react-navigation/native';
import { createStyles } from '@/contexts/theme-context';
import { EmptyStateMinimal } from '@/components/empty-state-minimal';
import { FilterOption, SplitFilterList } from '@/components/split-filter-list';
import { FulfillmentAvailability } from '@/components/partners/fulfillment-availability';
import { HorizontalSelector } from '@/components/horizontal-selector';
import { ItemSearchHeaderAction } from '@/components/item-search-header-action';
import { OfflineComponent } from '@/components/offline-component';
import { PageContainer } from '@/components/page-container';
import { ScannerOperationsActionSheet } from '@/components/scanner-operations-action-sheet';
import { SCREEN_PADDING_SMALL, TYPOGRAPHY } from '@/styles';
import { ScrollViewProps, Text, View } from 'react-native';
import { SearchCatalogContent } from '@/components/search-catalog-content';
import { SearchFilterPills } from '@/components/search-filters/search-filter-pills';
import { SplitFilterContainer } from '@/components/split-filter-container';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCatalogCategoryItems } from '@/hooks/use-catalog-category-items';
import { useCatalogCategoryListsEnabled } from '@/hooks/dynamic-config/use-catalog-category-lists-enabled';
import { useCategoryCountsMap } from '@/hooks/use-category-counts-map';
import { useConsolidatedCatalogFilters } from '@/hooks/catalog-search/use-consolidated-catalog-filters';
import {
  useDynamicConfigValue,
  useSupplierDynamicConfigValue,
} from '@/hooks/use-dynamic-config-value';
import { useFilteredCategoryCatalog } from '@/hooks/catalog-search/use-filtered-category-catalog';
import { useFilteredSearchCatalog } from '@/hooks/catalog-search/use-filtered-search-catalog';
import { useGeneratedCategories } from '@/hooks/use-generated-categories';
import { useHeaderRestaurantSelectorProps } from '@/components/use-header-restaurant-selector-props';
import { useItemGroupBadgeFilters } from '@/hooks/use-item-group-badge-filters';
import { useItemRecommendationsContext } from '@/contexts/item-recommendations-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { usePromotionListOptions } from '@/hooks/use-promotion-list-options';
import { useScannerOperations } from '@/hooks/use-scanner-operations';
import { useTabBlurListener } from '@/hooks/use-tab-blur-listener';
import { useUserContext } from '@/contexts/user-context';
import { useVariantPackGroupItems } from '@/hooks/use-catalog-variant-pack-group-items';
import { ViewOrderButton } from '@/components/partners/view-order-button';

interface Props {
  navigation: CompositeNavigationProp<
    StackNavigationProp<CatalogStackParams, CATALOG_ROUTES>,
    StackNavigationProp<AuthStackParams>
  >;
}
export const CatalogSearchScreen: React.FC<Props> = ({ navigation }) => {
  const baseNavHeaderProps = useHeaderRestaurantSelectorProps();
  const [styles, theme] = useStyles();

  const { itemRecommendations } = useItemRecommendationsContext();
  const { isSmallLayout, isSmallTabletLayout } = useLayoutContext();
  const { isGuestMode } = useUserContext();
  const isOfflineModeActive = useOfflineModeActive();
  const promotions = usePromotionListOptions();
  const scannerOperations = useScannerOperations(CATALOG_ROUTES);
  const orderGuideUnifiedSearchEnabled = useSupplierDynamicConfigValue(
    'order_guide_unified_search_enabled',
  );
  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');
  const catalogCategoryListsEnabled = useCatalogCategoryListsEnabled();

  const [showScanSelectionActionSheet, setShowScanSelectionActionSheet] =
    useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<CatalogCategoryType>();
  const [searchText, setSearchText] = useState<string | null>(null);

  const { categoryItemsList, hasResponse: categoryItemsHasResponse } =
    useCatalogCategoryItems();
  const { variantPackGroupItemsList, hasResponse: groupItemsHasResponse } =
    useVariantPackGroupItems();

  const searchCatalog = useFilteredSearchCatalog({
    category: selectedCategory,
  });
  const categoryCatalog = useFilteredCategoryCatalog({
    category: catalogCategoryListsEnabled ? undefined : selectedCategory,
  });
  const { badges } = useItemGroupBadgeFilters({
    category: selectedCategory,
    items: catalogCategoryListsEnabled
      ? (searchCatalog.items ?? undefined)
      : categoryCatalog.listData,
  });

  const {
    changeSortOrder,
    clearSearch,
    items,
    searchFilters,
    searchItems,
    searchResults,
    searchSortOrder,
    queryLoading,
    queryLoadingMore,
    fetchNextPage,
  } = searchCatalog;

  useTabBlurListener(
    useCallback(() => {
      setSearchText(null);
      setSelectedCategory(undefined);
      clearSearch();
    }, [clearSearch]),
  );

  const showSearchView =
    Boolean(searchText) || Boolean(searchFilters.selected.length);

  const showCategoryCounts = catalogCategoryListsEnabled
    ? Boolean(searchText)
    : showSearchView;

  const catalogFilters = useConsolidatedCatalogFilters({
    badges,
    catalogCategoryListsEnabled,
    categoryCatalog,
    searchCatalog,
    showSearchView,
  });

  const hasResponse = itemGroupsEnabled
    ? groupItemsHasResponse
    : categoryItemsHasResponse;

  const showSideBar = !isSmallLayout && !isSmallTabletLayout;

  const { categories } = useGeneratedCategories();

  const categoryCountsMap = useCategoryCountsMap({
    categories,
    searchResults: searchResults ?? [],
  });

  const onSelectCategory = useCallback(
    (category: CatalogCategoryType) => {
      if (isSmallLayout) {
        navigation.push(CATALOG_ROUTES.Search, {
          category: category,
          showCategories: true,
        });
      } else {
        setSelectedCategory(category);
      }
    },
    [isSmallLayout, navigation],
  );

  const filterItems = useMemo<FilterOption[]>(() => {
    if (!hasResponse) {
      return [];
    }

    const filters: FilterOption[] = [];

    if (!isSmallLayout) {
      filters.push({
        metadata: {
          action: () => {
            // only drop the category, the current query is kept on purpose to be used in searching 'All' category
            setSelectedCategory(undefined);
          },
          ...(showCategoryCounts && {
            count: categoryCountsMap.get(ALL_CATEGORY_VALUE),
          }),
          isSelected: !selectedCategory,
        },
        title: ALL_CATEGORIES_FILTER,
        value: ALL_CATEGORY_VALUE,
      });
    }

    if (promotions.length) {
      filters.push({
        metadata: {
          action: () => {
            navigation.push(CATALOG_ROUTES.VolumePromotionsList);
          },
        },
        title: i18n.t('promotions'),
        value: 'promotions',
      });
    }

    if (itemRecommendations.length) {
      filters.push({
        metadata: {
          action: () => {
            navigation.push(CATALOG_ROUTES.ItemRecommendationsList);
          },
        },
        title: i18n.t('recommendations'),
        value: 'item_recs',
      });
    }

    if (itemGroupsEnabled) {
      for (const { category } of variantPackGroupItemsList) {
        filters.push({
          metadata: {
            action: () => {
              onSelectCategory(category);
            },
            ...(showCategoryCounts && {
              count: category.uuid ? categoryCountsMap.get(category.uuid) : 0,
            }),
            isSelected: category.uuid === selectedCategory?.uuid,
          },
          title: category.name || i18n.t('other'),
          value: category.uuid || NULL_CATEGORY_VALUE,
        });
      }
    } else {
      for (const { category } of categoryItemsList) {
        filters.push({
          metadata: {
            action: () => {
              onSelectCategory(category);
            },
            ...(showCategoryCounts && {
              count: categoryCountsMap.get(
                category.name ?? NULL_CATEGORY_VALUE,
              ),
            }),
            isSelected: category.uuid === selectedCategory?.uuid,
          },
          title: category.name || i18n.t('other'),
          value: category.name || NULL_CATEGORY_VALUE,
        });
      }
    }

    return filters;
  }, [
    categoryCountsMap,
    categoryItemsList,
    hasResponse,
    isSmallLayout,
    itemGroupsEnabled,
    itemRecommendations.length,
    navigation,
    onSelectCategory,
    promotions.length,
    selectedCategory,
    variantPackGroupItemsList,
    showCategoryCounts,
  ]);

  const sections = useMemo(
    () => [
      {
        data: itemGroupsEnabled ? variantPackGroupItemsList : categoryItemsList,
        key: null,
      },
    ],
    [categoryItemsList, itemGroupsEnabled, variantPackGroupItemsList],
  );

  const sideBarVisible = showSideBar && filterItems.length > 1;

  const pageHeaderContainerProps = useMemo(() => {
    const onScanPress =
      scannerOperations.length > 0
        ? () => {
            scannerOperations.length > 1
              ? setShowScanSelectionActionSheet(true)
              : navigation.push(CATALOG_ROUTES.Scan);
          }
        : undefined;

    const actions = onScanPress
      ? [
          {
            Icon: ScanIcon,
            iconProps: {
              stroke: theme.sk.color.icon.primary,
            },
            onPress: onScanPress,
            testID: 'scan-barcode-icon',
          },
        ]
      : [];

    const onSearchPress = () => {
      if (orderGuideUnifiedSearchEnabled) {
        navigation.push(CATALOG_ROUTES.CatalogUnifiedSearch);
      }
    };

    return {
      navigationHeaderProps: {
        ...baseNavHeaderProps,
        actions: isOfflineModeActive
          ? []
          : [
              ...actions,
              isSmallLayout
                ? {
                    Icon: SearchIcon,
                    iconProps: {
                      fill: theme.colors.primary,
                    },
                    onPress: () => {
                      navigation.push(CATALOG_ROUTES.Search, {
                        showCategories: true,
                      });
                    },
                    testID: 'search-icon',
                  }
                : {
                    component: (
                      <ItemSearchHeaderAction
                        containerStyle={
                          onScanPress ? styles.scannerMargin : null
                        }
                        editable={!orderGuideUnifiedSearchEnabled}
                        onPress={onSearchPress}
                        onSubmit={async (value: string | null) => {
                          setSearchText(value);
                          if (value || Boolean(searchFilters.selected.length)) {
                            await searchItems(value || '', searchSortOrder);
                          } else {
                            clearSearch();
                          }
                        }}
                      />
                    ),
                  },
            ],
        animate: !showSideBar,
        backButtonLabel: isSmallLayout ? undefined : i18n.t('back'),
        centerTitle: isGuestMode && isSmallLayout,
        contentFooter: isGuestMode ? null : <FulfillmentAvailability />,
        responsive: true,
        showBackButton: isGuestMode && isSmallLayout,
        subheader: (
          <>
            {sideBarVisible ? null : (
              <>
                <View style={styles.categoryBorder}>
                  <HorizontalSelector<FilterOption>
                    data={filterItems}
                    keyExtractor={(item) => String(item.value)}
                    onItemPress={(item) => {
                      item?.metadata!.action();
                    }}
                    selectedItem={
                      filterItems.find(
                        (i) =>
                          i.value ===
                          (selectedCategory?.uuid || ALL_CATEGORY_VALUE),
                      ) ?? null
                    }
                    trackingKey="catalog-screen-sections"
                  />
                </View>
                {selectedCategory &&
                selectedCategory.uuid !== ALL_CATEGORY_VALUE ? (
                  <SearchFilterPills
                    category={selectedCategory}
                    filters={catalogFilters}
                    onChangeSortOrder={changeSortOrder}
                    sortOrder={
                      showSearchView || catalogCategoryListsEnabled
                        ? searchSortOrder
                        : undefined
                    }
                  />
                ) : null}
              </>
            )}
          </>
        ),
        title: i18n.t('catalog'),
      },
      statusBarColor: theme.colors.background,
    };
  }, [
    baseNavHeaderProps,
    catalogFilters,
    changeSortOrder,
    clearSearch,
    filterItems,
    sideBarVisible,
    isGuestMode,
    isOfflineModeActive,
    isSmallLayout,
    navigation,
    scannerOperations,
    searchFilters,
    searchItems,
    searchSortOrder,
    showSearchView,
    showSideBar,
    selectedCategory,
    styles,
    theme,
    orderGuideUnifiedSearchEnabled,
    catalogCategoryListsEnabled,
  ]);

  const content = (scrollViewProps: ScrollViewProps) => {
    if (!hasResponse) {
      if (isOfflineModeActive) {
        return (
          <View
            style={[
              styles.staticContentContainer,
              sideBarVisible ? null : scrollViewProps.contentContainerStyle,
            ]}
          >
            <OfflineComponent />
          </View>
        );
      }

      return (
        <View
          style={[
            styles.staticContentContainer,
            isSmallLayout ? styles.contentContainerStyleSmall : null,
            sideBarVisible ? null : scrollViewProps.contentContainerStyle,
          ]}
          testID="loading-indicator"
        >
          {!selectedCategory && !searchText ? (
            <CatalogCarouselsLoading />
          ) : (
            <CatalogItemListLoading />
          )}
        </View>
      );
    }

    if (
      categoryItemsList.length === 0 &&
      variantPackGroupItemsList.length === 0
    ) {
      return (
        <EmptyStateMinimal
          containerStyle={
            sideBarVisible ? null : scrollViewProps.contentContainerStyle
          }
          ImageComponent={CatalogEmptyState}
          text={i18n.t('partner_supplier_catalog_empty_state.message')}
        />
      );
    }

    if (!selectedCategory && !searchText && !searchFilters.selected.length) {
      return (
        <CategoryCarousels
          contentContainerStyle={
            sideBarVisible ? null : scrollViewProps.contentContainerStyle
          }
          onSelectCategory={onSelectCategory}
          scrollViewProps={scrollViewProps}
          sections={sections}
        />
      );
    }

    return (
      <>
        {sideBarVisible ? (
          <View style={styles.catalogFiltersWrapper}>
            <SearchFilterPills
              category={selectedCategory}
              filters={catalogFilters}
              onChangeSortOrder={changeSortOrder}
              sortOrder={
                showSearchView || catalogCategoryListsEnabled
                  ? searchSortOrder
                  : undefined
              }
            />
            {searchText ? (
              <View style={styles.searchText}>
                <Text style={TYPOGRAPHY.h2}>"{searchText}"</Text>
              </View>
            ) : null}
          </View>
        ) : null}
        {!showSearchView && selectedCategory && !catalogCategoryListsEnabled ? (
          <CatalogCategoryList
            categoryCatalog={categoryCatalog}
            scrollViewProps={{ ...(!sideBarVisible && scrollViewProps) }}
          />
        ) : (
          <SearchCatalogContent
            items={items}
            loading={queryLoading}
            loadingMore={queryLoadingMore}
            onEndReached={() => fetchNextPage(searchSortOrder)}
            renderSource="search_supplier_catalog"
            scrollViewProps={{ ...(!sideBarVisible && scrollViewProps) }}
            searchText={searchText || ''}
          />
        )}
      </>
    );
  };

  return (
    <>
      <PageContainer {...pageHeaderContainerProps} animateNavigationHeader>
        {(scrollViewProps) => (
          <SplitFilterContainer
            FilterComponent={
              !hasResponse ? (
                <CatalogSidebarLoading />
              ) : (
                <SplitFilterList
                  contentContainerStyle={styles.splitFilterContainer}
                  data={filterItems}
                  scrollEnabled
                />
              )
            }
            filtersVisible={(!hasResponse && showSideBar) || sideBarVisible}
          >
            {content(scrollViewProps)}
            <ViewOrderButton />
          </SplitFilterContainer>
        )}
      </PageContainer>
      {showScanSelectionActionSheet ? (
        <ScannerOperationsActionSheet
          onClose={() => setShowScanSelectionActionSheet(false)}
          operations={scannerOperations}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  catalogFiltersWrapper: {
    marginLeft: SCREEN_PADDING_SMALL,
  },
  categoryBorder: {
    borderTopColor: theme.colors.stroke,
    borderTopWidth: 1,
  },
  contentContainerStyleSmall: {
    backgroundColor: theme.colors.background,
  },
  scannerMargin: {
    marginLeft: 12,
  },
  searchText: {
    paddingHorizontal: 16,
    paddingTop: 12,
  },
  splitFilterContainer: {
    marginTop: 8,
    paddingRight: 8,
  },
  staticContentContainer: {
    flex: 1,
    marginTop: 24,
  },
}));
